import { getImage } from 'gatsby-plugin-image';
import {
  PLATFORM_BG_COLOR_MAP,
  SOCIAL_PLATFORM,
  SOCIAL_PLATFORMS,
} from '../../constants';

const DEFAULT_LINK_ORDER = 100;

export const formatLinksV2 = (pageContext, increment = 0) => {
  const links = [];
  const linkOptions = {
    withSensitiveLinkHoldToOpen: Boolean(
      pageContext.data.Sensitive_Link_Hold_To_Open
    ),
  };

  for (const platform of SOCIAL_PLATFORMS) {
    const linkData = processPlatformLink(
      platform,
      pageContext,
      increment,
      linkOptions
    );
    if (linkData) links.push(linkData);
  }

  for (const custom of pageContext.customLinks.edges) {
    const linkData = processCustomLink(custom, increment, linkOptions);
    if (linkData) links.push(linkData);
  }

  return sortLinksByOrder(links);
};

export const formatLinksV3 = (pageContext, increment = 0) => {
  const links = [];
  const photosArray = pageContext.data.Photo_Gallery?.localFiles?.map(
    (photo) => photo?.childImageSharp
  );

  const linkOptions = {
    includeBackground: true,
    includeBackgroundColor: true,
    requireBackground: true,
    photosArray,
    withSensitiveLinkHoldToOpen: Boolean(
      pageContext.data.Sensitive_Link_Hold_To_Open
    ),
  };

  const v3SmallLinksConfig = new Set(
    Object.keys(pageContext.v3SmallLinksConfig || {})
  );

  for (const platform of SOCIAL_PLATFORMS) {
    if (pageContext.data.V3 && v3SmallLinksConfig.has(platform)) {
      continue;
    }

    const linkData = processPlatformLink(
      platform,
      pageContext,
      increment,
      linkOptions
    );
    if (linkData) {
      links.push(linkData);
    }
  }

  for (const custom of pageContext.customLinks.edges) {
    const linkData = processCustomLink(custom, increment, linkOptions);
    if (linkData) {
      links.push(linkData);
    }
  }

  return sortLinksByOrder(links);
};

export const formatIncrementedValue = (value, increment = 0) => {
  if (!value) {
    return { before: value, number: null, after: null };
  }

  const match = value.match(/\{\s*(?<number>\d+)\s*\}/);

  if (match) {
    const number = Number(match.groups?.number);
    if (!Number.isNaN(number)) {
      return {
        before: value.substring(0, match.index),
        number: Math.max(number + increment, 0),
        after: value.substring(match.index + match[0].length),
      };
    }
  }

  return { before: value, number: null, after: null };
};

const getPlatformOrder = (platform, pageContext) => {
  const platformOrderRaw =
    platform === SOCIAL_PLATFORM.ONLYFANS
      ? 0
      : (pageContext.data[`${platform}_Order`] ?? DEFAULT_LINK_ORDER);

  return Number(platformOrderRaw);
};

const getCustomLinkOrder = (link) => {
  const linkOrderRaw = link.Order ?? DEFAULT_LINK_ORDER;
  return Number(linkOrderRaw);
};

const sortLinksByOrder = (links) => {
  return links.sort((a, b) => a.order - b.order);
};

const processPlatformLink = (
  platform,
  pageContext,
  increment,
  options = {}
) => {
  const {
    includeBackground = false,
    includeBackgroundColor = false,
    photosArray = null,
    withSensitiveLinkHoldToOpen = false,
  } = options;

  if (!Boolean(pageContext.data[platform])) {
    return null;
  }

  const platformLink = pageContext.data[platform];
  const platformTitle = formatIncrementedValue(
    pageContext.data[`${platform}PlatformTitle`] || '',
    increment
  );
  const platformOrder = getPlatformOrder(platform, pageContext);

  const linkData = {
    platform,
    link: platformLink,
    platformTitle,
    order: platformOrder,
    withSensitiveLinkHoldToOpen,
  };

  if (includeBackground) {
    const randomPhoto =
      photosArray?.[Math.floor(Math.random() * photosArray?.length)];

    const platformBackground =
      getImage(
        pageContext.data[`${platform}PlatformBackground`]?.localFiles?.[0]
          ?.childImageSharp
      ) || randomPhoto;

    linkData.backgroundImage = platformBackground;
  }

  if (includeBackgroundColor) {
    linkData.backgroundColor = PLATFORM_BG_COLOR_MAP[platform];
  }

  return linkData;
};

const processCustomLink = (customLinkNode, increment, options = {}) => {
  const {
    requireBackground = false,
    includeBackgroundColor = false,
    photosArray = null,
    withSensitiveLinkHoldToOpen = false,
  } = options;

  const link = customLinkNode.node.data;

  if (!link['Custom_Link_Icon']?.localFiles) {
    return null;
  }
  if (requireBackground && !link['Custom_Link_Background']?.localFiles) {
    return null;
  }

  const linkIcon = getImage(
    link['Custom_Link_Icon']?.localFiles?.[0]?.childImageSharp
  );
  const linkUrl = link['Custom_Link_URL'];
  const linkPlatformTitle = formatIncrementedValue(
    link['Custom_Link_Text'] || '',
    increment
  );
  const linkOrder = getCustomLinkOrder(link);
  const withDisclaimer = Boolean(link['Sensitive_Content_Warning']);

  const linkData = {
    platform: null,
    icon: linkIcon,
    link: linkUrl,
    platformTitle: linkPlatformTitle,
    order: linkOrder,
    withDisclaimer,
    withSensitiveLinkHoldToOpen,
  };

  if (!requireBackground && link['Custom_Link_Background']?.localFiles) {
    linkData.backgroundImage = getImage(
      link['Custom_Link_Background']?.localFiles?.[0]?.childImageSharp
    );
  }

  if (requireBackground) {
    const randomPhoto =
      photosArray?.[Math.floor(Math.random() * photosArray?.length)];

    const linkBackground =
      getImage(
        link['Custom_Link_Background']?.localFiles?.[0]?.childImageSharp
      ) || randomPhoto;

    linkData.backgroundImage = linkBackground;
  }

  if (includeBackgroundColor) {
    linkData.backgroundColor = '#fff';
  }

  return linkData;
};

export const getV3SmallLinks = (pageContext) => {
  if (!pageContext.data.V3 || !pageContext.v3SmallLinksConfig) {
    return [];
  }

  const smallLinks = [];

  for (const platform of SOCIAL_PLATFORMS) {
    const url = pageContext.data[platform];
    const isSmall = pageContext.v3SmallLinksConfig[platform];

    if (url && isSmall) {
      smallLinks.push({ platform, link: url });
    }
  }

  return smallLinks;
};
